import { includes } from 'lodash';
import { useMemo } from 'react';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareCard } from '@/components/form/formAwareInputs/FormAwareCard';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { Card, CardProps } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { MultiDocumentUploaderWithListV2 } from '@/modules/documents/MultiDocumentUploader/MultiDocumentUploaderWithListV2';
import { NonInsuranceAssets } from '@/modules/entities/EntityShortFormModal/CreateEntityShortForm/fields/NonInsuranceAssets';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getEntitySelectOptions } from '@/utils/entities/getEntitySelectOptions';

import {
  ACCOUNT_ENTITY_TYPES,
  BUSINESS_ENTITY_TYPES,
  ENTITY_TYPES,
  NON_TRUST_CHARITABLE_ENTITY_TYPES,
  PERSONAL_FAMILY_ACCOUNT_TYPES,
} from '../../entities.constants';
import { EntityType } from '../../types/EntityType';
import { CreateEntityShortFormContext } from './context/CreateEntityShortForm.context';
import { OWNERSHIP_PERCENTAGE_ENTITY_TYPES } from './CreateEntityShortForm.constants';
import {
  EntityShortFormPaths,
  EntityShortFormShape,
  PrincipalsOwnershipMap,
} from './CreateEntityShortForm.types';
import { AssetLocation } from './fields/AssetLocation';
import { Assets } from './fields/Assets';
import { BusinessEntityPrincipals } from './fields/BusinessEntityPrincipals';
import { InfoCallout } from './fields/InfoCallout';
import { Principals } from './fields/Principals';
import { validateMoreThanOnePrincipalSelected } from './fields/Principals.utils';
import { entityTypeAllowsMultipleSelection } from './formUtils/CreateEntityShortForm.common';

interface CreateEntityShortFormProps {
  householdId: string;
  principalOptions: SelectInputOption<string>[];
  forceEntityType?: boolean;
  /**
   * If provided, limit the entity types that can be created to the provided types.
   */
  limitEntityTypes?: EntityType[];
  entityTypeLabel?: string;
}

const CARD_PROPS: CardProps = {
  variant: 'filled',
  sx: { p: 2, height: '100%' },
};

export function CreateEntityShortForm({
  householdId,
  principalOptions,
  forceEntityType,
  limitEntityTypes,
  entityTypeLabel = 'Entity type',
}: CreateEntityShortFormProps) {
  const { applicationConfiguration } = useTenantDetailsContext();

  const { control, watch } = useFormContext<EntityShortFormShape>();
  const entityFormOptions = useMemo(
    () => getEntitySelectOptions(limitEntityTypes),
    [limitEntityTypes]
  );
  const entityType =
    watch('createEntityShortForm.entityType') || 'irrevocable-trust';

  const isNonTrustCharitableEntity =
    NON_TRUST_CHARITABLE_ENTITY_TYPES.includes(entityType);
  const shouldHideAssetLocation =
    ACCOUNT_ENTITY_TYPES.includes(entityType) ||
    BUSINESS_ENTITY_TYPES.includes(entityType) ||
    entityType == 'crt' ||
    entityType === 'insurance-account';
  const shouldCollectOwnershipPercentage =
    OWNERSHIP_PERCENTAGE_ENTITY_TYPES.includes(entityType);
  const shouldRenderFullRowPrincipalsInput =
    isNonTrustCharitableEntity ||
    shouldCollectOwnershipPercentage ||
    shouldHideAssetLocation;
  const shouldShowDocumentUploader =
    !applicationConfiguration?.disableAiCapabilities && // Don't show the doc uploader if tenant has disabled AI capabilities
    entityType !== ENTITY_TYPES.SOLE_PROPRIETORSHIP &&
    entityType !== ENTITY_TYPES.GRAT && // Don't show document uploader for GRAT
    !PERSONAL_FAMILY_ACCOUNT_TYPES.includes(entityType);

  return (
    <CreateEntityShortFormContext.Provider value={{ isShortForm: true }}>
      <ModalFormLayout>
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTextInput<EntityShortFormShape>
              control={control}
              fieldName={
                'createEntityShortForm.name' as const satisfies EntityShortFormPaths
              }
              label="Display name"
              required
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTypeaheadSelectInput<EntityShortFormShape>
              control={control}
              options={entityFormOptions}
              fieldName={
                'createEntityShortForm.entityType' as const satisfies EntityShortFormPaths
              }
              label={entityTypeLabel}
              groupBy={(option) => option.groupName ?? ''}
              required
              disabled={forceEntityType}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        {entityType !== 'grat' && (
          <>
            <FormLayoutRow>
              <FormLayoutItem
                width={shouldRenderFullRowPrincipalsInput ? 12 : 5}
              >
                {includes(BUSINESS_ENTITY_TYPES, entityType) ? (
                  <Card {...CARD_PROPS}>
                    <BusinessEntityPrincipals
                      entityType={entityType}
                      householdId={householdId}
                    />
                  </Card>
                ) : (
                  <FormAwareCard<EntityShortFormShape>
                    {...CARD_PROPS}
                    control={control}
                    validation={{
                      minLength: (value) => {
                        if (!entityTypeAllowsMultipleSelection(entityType)) {
                          return undefined;
                        }

                        return validateMoreThanOnePrincipalSelected(
                          value as PrincipalsOwnershipMap | undefined,
                          undefined, // no single client selection in this scenario, since where on the multi-client field
                          entityType
                        );
                      },
                    }}
                    fieldName={
                      `createEntityShortForm.principals` as const satisfies EntityShortFormPaths
                    }
                  >
                    <Principals
                      entityType={entityType}
                      principalOptions={principalOptions}
                    />
                  </FormAwareCard>
                )}
              </FormLayoutItem>
              {!shouldRenderFullRowPrincipalsInput && (
                <FormLayoutItem width={7}>
                  <Card variant="filled" sx={{ p: 2, height: '100%' }}>
                    <AssetLocation entityType={entityType} />
                  </Card>
                </FormLayoutItem>
              )}
            </FormLayoutRow>
            {shouldRenderFullRowPrincipalsInput && !shouldHideAssetLocation && (
              <FormLayoutRow>
                <FormLayoutItem
                  // just toggle the visibility rather than unmounting to keep the input registered
                  // so the value can be changed programmatically
                  sx={{
                    display: isNonTrustCharitableEntity ? 'none' : undefined,
                  }}
                  width={12}
                >
                  <Card variant="filled" sx={{ p: 2, height: '100%' }}>
                    <AssetLocation entityType={entityType} />
                  </Card>
                </FormLayoutItem>
              </FormLayoutRow>
            )}
            {entityType === 'qprt' && (
              <FormLayoutRow>
                <FormLayoutItem>
                  <Assets
                    variant="initialFunding"
                    householdId={householdId}
                    entityType={entityType}
                  />
                </FormLayoutItem>
              </FormLayoutRow>
            )}
            {entityType !== 'insurance-account' && entityType !== 'ilit' && (
              <FormLayoutRow>
                <FormLayoutItem>
                  <Assets householdId={householdId} entityType={entityType} />
                </FormLayoutItem>
              </FormLayoutRow>
            )}
          </>
        )}
        {entityType === 'ilit' && <NonInsuranceAssets />}
        {shouldShowDocumentUploader && householdId && (
          <FormLayoutRow>
            <FormLayoutItem>
              <MultiDocumentUploaderWithListV2
                householdId={householdId}
                allowSelectExistingDocuments={true}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        )}
        <InfoCallout entityType={entityType} />
      </ModalFormLayout>
    </CreateEntityShortFormContext.Provider>
  );
}
